class JohnDiamondHeader {
  constructor(header) {
    this.header = header;
    this.toggle = header.querySelector('[data-menu-toggle]');
    this.navigation = header.querySelector('[data-mobile-navigation]');
    this.shopMenu = header.querySelector('[data-shop-menu]');
    this.onToggle = this.onToggle.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);

    this.toggle?.addEventListener('click', this.onToggle);
    this.navigation?.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) this.setMenuState(false);
    });
    this.shopMenu?.addEventListener('toggle', () => {
      this.header.classList.toggle('is-menu-open', this.shopMenu.open);
    });
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('pointerdown', this.onDocumentPointerDown);

    if (header.dataset.transparent === 'true') {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
  }

  onToggle() {
    const isOpen = this.toggle.getAttribute('aria-expanded') === 'true';
    if (!isOpen && this.shopMenu?.open) this.shopMenu.open = false;
    this.setMenuState(!isOpen);
  }

  onDocumentPointerDown(event) {
    if (!this.shopMenu?.open || this.shopMenu.contains(event.target)) return;
    this.shopMenu.open = false;
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.shopMenu?.open) {
      this.shopMenu.open = false;
      this.shopMenu.querySelector('summary')?.focus();
      return;
    }

    if (event.key === 'Escape' && this.toggle?.getAttribute('aria-expanded') === 'true') {
      this.setMenuState(false);
      this.toggle.focus();
    }
  }

  onScroll() {
    this.header.classList.toggle('is-scrolled', window.scrollY > 24);
  }

  setMenuState(isOpen) {
    this.toggle.setAttribute('aria-expanded', String(isOpen));
    this.navigation.hidden = !isOpen;
    this.header.classList.toggle('is-menu-open', isOpen);
    document.body.classList.toggle('is-navigation-open', isOpen);
  }
}

document.documentElement.classList.remove('no-js');

document.querySelectorAll('[data-site-header]').forEach((header) => {
  new JohnDiamondHeader(header);
});

class JohnDiamondProductForm {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector('.product-form');
    this.variantInput = section.querySelector('[data-variant-id]');
    this.optionGroups = [...section.querySelectorAll('[data-option-group]')];
    this.variantScript = section.querySelector('[data-product-variants]');
    this.price = section.querySelector('[data-product-price]');
    this.mobilePrice = section.querySelector('[data-mobile-product-price]');
    this.availability = section.querySelector('[data-product-availability]');
    this.sku = section.querySelector('[data-product-sku]');
    this.submit = section.querySelector('[data-product-submit]');
    this.submitLabel = section.querySelector('[data-product-submit-label]');
    this.inquiry = section.querySelector('[data-product-inquiry]');
    this.message = section.querySelector('[data-product-message]');

    if (!this.form || !this.variantScript || this.form.dataset.initialized === 'true') return;

    try {
      this.variants = JSON.parse(this.variantScript.textContent);
    } catch (error) {
      this.variants = [];
    }

    this.form.dataset.initialized = 'true';
    this.optionGroups.forEach((group) => {
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        button.addEventListener('click', () => this.onOptionSelect(group, button));
      });
    });

    this.updateOptionAvailability();
  }

  onOptionSelect(group, selectedButton) {
    group.dataset.selectedValue = selectedButton.dataset.optionValue;
    group.querySelector('[data-option-current]').textContent = selectedButton.dataset.optionValue;
    group.querySelectorAll('[data-option-value]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button === selectedButton));
    });

    const selectedOptions = this.optionGroups.map((optionGroup) => optionGroup.dataset.selectedValue);
    const variant = this.variants.find((candidate) => (
      candidate.options.every((option, index) => option === selectedOptions[index])
    ));

    this.updateVariant(variant);
  }

  updateVariant(variant) {
    const isAvailable = Boolean(variant?.available);

    if (!variant) {
      if (this.submit) this.submit.disabled = true;
      if (this.submitLabel) this.submitLabel.textContent = 'Configuration unavailable';
      if (this.availability) this.availability.textContent = 'Configuration unavailable';
      if (this.message) this.message.textContent = 'Please choose another configuration or request private assistance.';
      this.inquiry?.classList.remove('is-hidden');
      return;
    }

    if (this.variantInput) this.variantInput.value = variant.id;
    if (this.price && variant.priceFormatted) this.price.textContent = variant.priceFormatted;
    if (this.mobilePrice && variant.priceFormatted) this.mobilePrice.textContent = variant.priceFormatted;
    if (this.sku) this.sku.textContent = variant.sku || 'JD-FLR-STAGED';
    if (this.submit) this.submit.disabled = !isAvailable;
    if (this.submitLabel) this.submitLabel.textContent = isAvailable ? 'Add to bag' : 'Currently unavailable';
    if (this.availability) this.availability.textContent = isAvailable ? 'Available to order' : 'Currently unavailable';
    if (this.message) this.message.textContent = isAvailable ? '' : 'This configuration is displayed for planning and is not currently in stock.';
    this.inquiry?.classList.toggle('is-hidden', isAvailable);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    if (variant.featuredMediaId) {
      const media = this.section.querySelector(`[data-product-media-id="${variant.featuredMediaId}"]`);
      media?.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest' });
    }

    this.updateOptionAvailability();
  }

  updateOptionAvailability() {
    this.optionGroups.forEach((group) => {
      const position = Number(group.dataset.optionPosition) - 1;
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        const hasAvailableVariant = this.variants.some((variant) => (
          variant.options[position] === button.dataset.optionValue && variant.available
        ));
        button.classList.toggle('is-sold-out', !hasAvailableVariant);
        button.dataset.optionSoldOut = String(!hasAvailableVariant);
      });
    });
  }
}

function initializeProductForms(root = document) {
  root.querySelectorAll('[data-product-section]').forEach((section) => {
    new JohnDiamondProductForm(section);
  });
}

initializeProductForms();

class JohnDiamondStagedProduct {
  constructor(section) {
    this.section = section;
    this.groups = [...section.querySelectorAll('[data-staged-option-group]')];
    this.price = section.querySelector('[data-staged-price]');
    this.mobilePrice = section.querySelector('[data-mobile-staged-price]');
    this.availability = section.querySelector('[data-staged-availability]');
    this.sku = section.querySelector('[data-staged-sku]');
    this.addButton = section.querySelector('[data-staged-add]');
    this.inquiry = section.querySelector('[data-staged-inquiry]');
    this.message = section.querySelector('[data-staged-message]');
    if (section.dataset.stagedInitialized === 'true') return;
    section.dataset.stagedInitialized = 'true';
    this.root = window.Shopify?.routes?.root || '/';
    this.loading = true;
    this.product = null;
    this.busy = false;
    this.message?.setAttribute('role', 'status');
    this.message?.setAttribute('aria-live', 'polite');
    this.groups.forEach(group => group.querySelectorAll('[data-option-value]').forEach(button => {
      button.addEventListener('click', () => this.select(group, button));
    }));
    this.addButton?.addEventListener('click', () => this.addToBag());
    this.update();
    this.loadProduct();
  }
  async loadProduct() {
    const handles = { 'JD-FLR-DAH': 'dahlia', 'JD-FLR-CAM': 'camellia', 'JD-FLR-IRI': 'iris', 'JD-FLR-LIL': 'lily', 'JD-FLR-JAS': 'jasmine', 'JD-FLR-WIS': 'wisteria' };
    const handle = handles[this.section.dataset.stagedSku];
    try {
      if (!handle) throw new Error('Unknown Floral design');
      const response = await fetch(this.root + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Product not available');
      this.product = await response.json();
    } catch (error) {
      this.product = null;
    } finally {
      this.loading = false;
      this.update();
    }
  }
  select(group, button) {
    if (this.busy) return;
    group.dataset.selectedValue = button.dataset.optionValue;
    group.querySelector('[data-option-current]').textContent = button.dataset.optionValue;
    group.querySelectorAll('[data-option-value]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
    this.update();
  }
  selectedVariant() {
    const metal = this.groups.find(group => group.dataset.stagedOption === 'metal')?.dataset.selectedValue;
    const size = this.groups.find(group => group.dataset.stagedOption === 'size')?.dataset.selectedValue;
    return this.product?.variants?.find(variant => variant.option1 === metal && String(variant.option2) === String(size));
  }
  update() {
    const variant = this.selectedVariant();
    const available = Boolean(variant?.available);
    const metal = this.groups.find(group => group.dataset.stagedOption === 'metal')?.dataset.selectedValue;
    const fallbackPrice = metal === '925 Sterling Silver' ? this.section.dataset.silverPrice : this.section.dataset.vermeilPrice;
    const price = variant ? new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(variant.price / 100) : fallbackPrice;
    if (this.price) this.price.textContent = price;
    if (this.mobilePrice) this.mobilePrice.textContent = price;
    if (this.sku) this.sku.textContent = variant?.sku || 'By inquiry';
    if (this.availability) this.availability.textContent = this.loading ? 'Checking availability' : available ? 'Made to order' : 'Contact John to order';
    if (this.addButton) {
      this.addButton.disabled = this.loading || this.busy || !available;
      this.addButton.textContent = this.busy ? 'Adding...' : this.loading ? 'Checking availability' : available ? 'Add to bag' : 'Request this configuration';
    }
    this.inquiry?.classList.toggle('is-hidden', available || this.loading);
    if (this.message) this.message.textContent = this.loading ? '' : available ? 'Made to order for you.' : 'Contact John to confirm this configuration and production timing.';
  }
  async addToBag() {
    const variant = this.selectedVariant();
    if (this.busy || !variant?.available) return;
    this.busy = true;
    this.update();
    try {
      const response = await fetch(this.root + 'cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || 'Unable to add this piece. Please try again.');
      window.location.assign(this.root + 'cart');
    } catch (error) {
      this.busy = false;
      this.update();
      if (this.message) this.message.textContent = error.message || 'Unable to add this piece. Please try again.';
    }
  }
}

function initializeStagedProducts(root = document) {
  root.querySelectorAll('[data-staged-product]').forEach((section) => {
    new JohnDiamondStagedProduct(section);
  });
}

initializeStagedProducts();

class JohnDiamondStagedJewelry {
  constructor(section) {
    this.section = section;
    this.groups = [...section.querySelectorAll('[data-stage-option-group]')];
    this.price = section.querySelector('[data-stage-price]');
    this.mobilePrice = section.querySelector('[data-mobile-stage-price]');
    this.availability = section.querySelector('[data-stage-availability]');
    this.sku = section.querySelector('[data-stage-sku]');
    this.addButton = section.querySelector('[data-stage-add]');
    this.inquiry = section.querySelector('[data-stage-inquiry]');
    this.message = section.querySelector('[data-stage-message]');

    if (section.dataset.stageInitialized === 'true') return;
    section.dataset.stageInitialized = 'true';

    this.groups.forEach((group) => {
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        button.addEventListener('click', () => this.select(group, button));
      });
    });

    this.addButton?.addEventListener('click', () => {
      if (this.message) this.message.textContent = 'Catalog preview complete. Shopify checkout activates when the live product inventory is connected.';
    });
    this.update();
  }

  select(group, selectedButton) {
    group.dataset.selectedValue = selectedButton.dataset.optionValue;
    group.querySelector('[data-option-current]').textContent = selectedButton.dataset.optionValue;
    group.querySelectorAll('[data-option-value]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button === selectedButton));
    });
    this.update();
  }

  update() {
    const optionOne = this.groups.find((group) => group.dataset.stageOption === 'one')?.dataset.selectedValue;
    const optionTwo = this.groups.find((group) => group.dataset.stageOption === 'two')?.dataset.selectedValue;
    const firstIsAvailable = optionOne === this.section.dataset.stageOptionOneAvailable;
    const secondIsAvailable = optionTwo === this.section.dataset.stageOptionTwoAvailable;
    const isAvailable = firstIsAvailable && secondIsAvailable;
    const selectedButtons = this.groups.map((group) => (
      [...group.querySelectorAll('[data-option-value]')].findIndex((button) => button.dataset.optionValue === group.dataset.selectedValue) + 1
    ));

    if (this.price) this.price.textContent = firstIsAvailable ? this.section.dataset.stageBasePrice : this.section.dataset.stageAltPrice;
    if (this.mobilePrice) this.mobilePrice.textContent = firstIsAvailable ? this.section.dataset.stageBasePrice : this.section.dataset.stageAltPrice;
    if (this.sku) this.sku.textContent = `${this.section.dataset.stageSku}-${selectedButtons.map((number) => String(number).padStart(2, '0')).join('-')}`;
    if (this.availability) this.availability.textContent = isAvailable ? 'Available to order' : 'Currently unavailable';
    if (this.addButton) {
      this.addButton.disabled = !isAvailable;
      this.addButton.textContent = isAvailable ? 'Add to bag' : 'Currently unavailable';
    }
    this.inquiry?.classList.toggle('is-hidden', isAvailable);
    if (this.message) this.message.textContent = isAvailable ? '' : 'This configuration is displayed for planning and is not currently in stock.';
  }
}

function initializeStagedJewelry(root = document) {
  root.querySelectorAll('[data-staged-jewelry]').forEach((section) => {
    new JohnDiamondStagedJewelry(section);
  });
}

initializeStagedJewelry();

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function prepareHeroVideo(video) {
  if (video.dataset.heroVideoInitialized === 'true') return;

  video.dataset.heroVideoInitialized = 'true';
  video.autoplay = true;
  video.controls = false;
  video.defaultMuted = true;
  video.muted = true;
  video.playsInline = true;
  video.setAttribute('autoplay', '');
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.removeAttribute('controls');

  video.addEventListener('playing', () => {
    video.classList.add('is-playing');
    video.dataset.autoplayBlocked = 'false';
  });

  video.addEventListener('error', () => {
    video.classList.remove('is-playing');
  });

  ['loadeddata', 'canplay'].forEach((eventName) => {
    video.addEventListener(eventName, () => attemptHeroPlayback(video), { once: true });
  });
}

function attemptHeroPlayback(video) {
  prepareHeroVideo(video);

  if (reducedMotion.matches) {
    video.classList.remove('is-playing');
    video.pause();
    video.currentTime = 0;
    return;
  }

  if (document.hidden) return;

  video.controls = false;
  video.defaultMuted = true;
  video.muted = true;
  video.playsInline = true;

  const playback = video.play();
  if (playback?.catch) {
    playback.catch(() => {
      video.dataset.autoplayBlocked = 'true';
      video.classList.remove('is-playing');
    });
  }
}

function updateHeroVideos(root = document) {
  root.querySelectorAll('.media-hero__video').forEach((video) => {
    prepareHeroVideo(video);
    attemptHeroPlayback(video);
  });
}

updateHeroVideos();
reducedMotion.addEventListener?.('change', () => updateHeroVideos());
window.addEventListener('pageshow', () => updateHeroVideos());
window.addEventListener('focus', () => updateHeroVideos());
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) updateHeroVideos();
});

class JohnDiamondTestimonials {
  constructor(section) {
    this.section = section;
    this.track = section.querySelector('[data-testimonial-track]');
    this.slides = [...section.querySelectorAll('[data-testimonial-slide]')];
    this.buttons = [...section.querySelectorAll('[data-testimonial-button]')];

    if (!this.track || this.slides.length < 2 || section.dataset.testimonialsInitialized === 'true') return;

    section.dataset.testimonialsInitialized = 'true';
    this.frame = null;

    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.goTo(Number(button.dataset.testimonialIndex)));
    });

    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
  }

  goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;

    this.track.scrollTo({
      left: slide.offsetLeft,
      behavior: reducedMotion.matches ? 'auto' : 'smooth'
    });
    this.update(index);
  }

  onScroll() {
    if (this.frame) return;

    this.frame = window.requestAnimationFrame(() => {
      const activeIndex = this.slides.reduce((closest, slide, index) => (
        Math.abs(slide.offsetLeft - this.track.scrollLeft) < Math.abs(this.slides[closest].offsetLeft - this.track.scrollLeft)
          ? index
          : closest
      ), 0);

      this.update(activeIndex);
      this.frame = null;
    });
  }

  update(activeIndex) {
    this.buttons.forEach((button, index) => {
      const isActive = index === activeIndex;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', String(isActive));
    });
  }
}

function initializeTestimonials(root = document) {
  root.querySelectorAll('[data-trust-proof]').forEach((section) => {
    new JohnDiamondTestimonials(section);
  });
}

initializeTestimonials();

function initializeCollectionSorting(root = document) {
  root.querySelectorAll('[data-collection-sort]').forEach((select) => {
    if (select.dataset.initialized === 'true') return;
    select.dataset.initialized = 'true';
    select.addEventListener('change', () => select.form?.submit());
  });
}

initializeCollectionSorting();

document.addEventListener('shopify:section:load', (event) => {
  initializeProductForms(event.target);
  initializeStagedProducts(event.target);
  initializeStagedJewelry(event.target);
  updateHeroVideos(event.target);
  initializeTestimonials(event.target);
  initializeCollectionSorting(event.target);
});

/* Compact, native, non-autoplay commission galleries. */
(() => {
  if (window.JDCompactGalleries) { window.JDCompactGalleries.init(document); return; }
  const selector = '.custom-portfolio .custom-piece__gallery, .past-designs .past-design__gallery, [data-jd-gallery]';
  const instances = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let sequence = 0;
  function setup(track) {
    if (instances.has(track)) return;
    const slides = Array.from(track.children).filter(el => el.matches('figure, [data-jd-slide]'));
    if (!slides.length) return;
    const original = { id: track.id, tabindex: track.getAttribute('tabindex'), describedby: track.getAttribute('aria-describedby'), hook: track.hasAttribute('data-jd-gallery') };
    const wrapper = document.createElement('div');
    wrapper.className = 'jd-gallery';
    wrapper.setAttribute('role', 'group');
    wrapper.setAttribute('aria-roledescription', 'carousel');
    wrapper.setAttribute('aria-label', track.getAttribute('aria-label') || 'Jewelry gallery');
    track.before(wrapper); wrapper.append(track);
    if (!track.id) { do { track.id = 'jd-gallery-' + (++sequence); } while (document.querySelectorAll('[id="' + track.id + '"]').length > 1); }
    track.setAttribute('data-jd-gallery', '');
    track.classList.add('jd-gallery__viewport'); track.tabIndex = 0;
    slides.forEach(slide => slide.setAttribute('data-jd-slide', ''));
    const controls = document.createElement('div'); controls.className = 'jd-gallery__controls';
    const makeButton = (label, text) => {
      const button = document.createElement('button'); button.type = 'button'; button.className = 'jd-gallery__button';
      button.setAttribute('aria-label', label); button.setAttribute('aria-controls', track.id); button.textContent = text; return button;
    };
    const previous = makeButton('Previous image', '\u2190');
    const next = makeButton('Next image', '\u2192');
    const counter = document.createElement('span'); counter.className = 'jd-gallery__counter'; counter.setAttribute('aria-hidden', 'true');
    const status = document.createElement('span'); status.className = 'jd-gallery__sr-only'; status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite'); status.setAttribute('aria-atomic', 'true');
    const help = document.createElement('span'); help.id = track.id + '-help'; help.className = 'jd-gallery__sr-only';
    help.textContent = 'Use Left and Right arrow keys to browse images. Home goes to the first image; End goes to the last.';
    track.setAttribute('aria-describedby', [original.describedby, help.id].filter(Boolean).join(' '));
    controls.append(previous, counter, next);
    if (slides.length > 1) wrapper.append(controls, status, help);
    else { track.removeAttribute('aria-describedby'); if (original.describedby) track.setAttribute('aria-describedby', original.describedby); track.tabIndex = original.tabindex === null ? -1 : Number(original.tabindex); }
    let index = 0, target = 0, timer = 0, frame = 0, lastAnnounced = 0;
    const clamp = value => Math.max(0, Math.min(slides.length - 1, value));
    const center = el => { const rect = el.getBoundingClientRect(); return rect.left + rect.width / 2; };
    function readIndex() { const middle = center(track); let closest = Infinity; slides.forEach((slide, i) => { const distance = Math.abs(center(slide) - middle); if (distance < closest) { closest = distance; index = i; } }); }
    function render() { counter.textContent = (index + 1) + ' / ' + slides.length; previous.setAttribute('aria-disabled', String(target === 0)); next.setAttribute('aria-disabled', String(target === slides.length - 1)); }
    function go(value, animate = true) { target = clamp(value); render(); track.scrollBy({ left: center(slides[target]) - center(track), behavior: animate && !reducedMotion.matches ? 'smooth' : 'instant' }); }
    function settle() { readIndex(); target = index; render(); if (index !== lastAnnounced) { status.textContent = 'Image ' + (index + 1) + ' of ' + slides.length; lastAnnounced = index; } }
    function onScroll() { if (!frame) frame = requestAnimationFrame(() => { frame = 0; readIndex(); render(); }); clearTimeout(timer); timer = window.setTimeout(settle, 160); }
    function onKey(event) {
      if (event.target !== track || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const rtl = getComputedStyle(track).direction === 'rtl'; let destination;
      if (event.key === 'ArrowRight') destination = target + (rtl ? -1 : 1);
      else if (event.key === 'ArrowLeft') destination = target + (rtl ? 1 : -1);
      else if (event.key === 'Home') destination = 0;
      else if (event.key === 'End') destination = slides.length - 1;
      else return;
      event.preventDefault(); go(destination);
    }
    const onPrevious = () => go(target - 1), onNext = () => go(target + 1);
    const onPointer = () => { readIndex(); target = index; render(); };
    previous.addEventListener('click', onPrevious); next.addEventListener('click', onNext);
    track.addEventListener('scroll', onScroll, { passive: true }); track.addEventListener('keydown', onKey); track.addEventListener('pointerdown', onPointer, { passive: true });
    let observedWidth = track.clientWidth;
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => { const width = track.clientWidth; if (!width || width === observedWidth) return; observedWidth = width; go(target, false); }) : null;
    if (observer) observer.observe(track);
    readIndex(); target = index; lastAnnounced = index; render();
    instances.set(track, () => {
      if (observer) observer.disconnect(); clearTimeout(timer); cancelAnimationFrame(frame);
      previous.removeEventListener('click', onPrevious); next.removeEventListener('click', onNext);
      track.removeEventListener('scroll', onScroll); track.removeEventListener('keydown', onKey); track.removeEventListener('pointerdown', onPointer);
      track.classList.remove('jd-gallery__viewport'); if (!original.hook) track.removeAttribute('data-jd-gallery'); if (!original.id) track.removeAttribute('id');
      ['tabindex', 'describedby'].forEach(key => { const attribute = key === 'describedby' ? 'aria-describedby' : key; if (original[key] === null) track.removeAttribute(attribute); else track.setAttribute(attribute, original[key]); });
      wrapper.replaceWith(track); instances.delete(track);
    });
  }
  function init(root = document) { for (const [track, dispose] of instances) if (!track.isConnected) dispose(); if (root.matches && root.matches(selector)) setup(root); root.querySelectorAll(selector).forEach(setup); }
  window.JDCompactGalleries = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => { for (const [track, dispose] of instances) if (event.target.contains(track)) dispose(); });
})();
